import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import LandingReviews from '../components/LandingReviews';
import { Button } from '../components/ui/button';
import { MapPin, Calendar, Sparkles, Shield, ArrowRight, ChevronLeft, ChevronRight, Globe, Wallet, Users, Plane, FileText } from 'lucide-react';
import { useState, useRef, useMemo, useEffect } from 'react';

const HERO_BACKGROUNDS = [
  'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1920&q=80', // tropical beach turquoise
  'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=1920&q=80', // lake boat scenic
  'https://images.unsplash.com/photo-1519046904884-53103b34b206?w=1920&q=80', // beach clear sky
  'https://images.unsplash.com/photo-1506929562872-bb421503ef21?w=1920&q=80', // ocean bright blue
  'https://images.unsplash.com/photo-1473116763249-2faaef81ccda?w=1920&q=80', // sunset beach bright
  'https://images.unsplash.com/photo-1468413253725-0d5181091126?w=1920&q=80', // maldives clear water
];

const LandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const carouselRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const heroImage = useMemo(() => HERO_BACKGROUNDS[Math.floor(Math.random() * HERO_BACKGROUNDS.length)], []);

  // Capture referral code from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) sessionStorage.setItem('referral_code', ref.toUpperCase());
  }, []);

  const handleGetStarted = () => {
    navigate('/new-trip');
  };

  const handleDestinationClick = (destination) => {
    sessionStorage.setItem('selectedDestination', JSON.stringify({ city: destination.city, country: destination.country }));
    navigate('/new-trip');
  };

  const scrollCarousel = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = 320;
      const newScrollLeft = carouselRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      carouselRef.current.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
      setTimeout(() => {
        if (carouselRef.current) {
          setCanScrollLeft(carouselRef.current.scrollLeft > 0);
          setCanScrollRight(carouselRef.current.scrollLeft < carouselRef.current.scrollWidth - carouselRef.current.clientWidth - 10);
        }
      }, 300);
    }
  };

  const handleScroll = () => {
    if (carouselRef.current) {
      setCanScrollLeft(carouselRef.current.scrollLeft > 0);
      setCanScrollRight(carouselRef.current.scrollLeft < carouselRef.current.scrollWidth - carouselRef.current.clientWidth - 10);
    }
  };

  const features = [
    { icon: <Sparkles className="w-6 h-6" />, title: 'AI-Powered Planning', description: 'Get personalized itineraries crafted by intelligent algorithms based on your preferences.' },
    { icon: <Globe className="w-6 h-6" />, title: 'Multi-Country Trips', description: 'Plan trips spanning multiple countries and cities with seamless route optimization.' },
    { icon: <Wallet className="w-6 h-6" />, title: 'Budget Tracker', description: 'Track estimated costs, set budgets, and manage expenses for your entire trip.' },
    { icon: <FileText className="w-6 h-6" />, title: 'Visa & Documents', description: 'Get visa requirements, application links, and document checklists for every destination.' },
    { icon: <Calendar className="w-6 h-6" />, title: 'Day-by-Day Schedule', description: 'Detailed morning, afternoon, and evening activities perfectly organized.' },
    { icon: <MapPin className="w-6 h-6" />, title: 'Local Insights', description: 'Discover hidden gems, local restaurants, and free attractions at your destination.' },
    { icon: <Shield className="w-6 h-6" />, title: 'Travel Smart', description: 'Safety tips, weather forecasts, packing lists, and cultural etiquette guides.' },
    { icon: <Plane className="w-6 h-6" />, title: 'Book Flights & Hotels', description: 'Quick links to book flights and hotels at the best prices for your trip.' },
  ];

  const destinations = [
    { url: 'https://images.pexels.com/photos/20704787/pexels-photo-20704787.jpeg', city: 'Kyoto', country: 'Japan' },
    { url: 'https://images.unsplash.com/photo-1656677476420-7159cac2366a?w=800', city: 'Santorini', country: 'Greece' },
    { url: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=800', city: 'Paris', country: 'France' },
    { url: 'https://images.unsplash.com/photo-1538332576228-eb5b4c4de6f5?w=800', city: 'New York', country: 'United States' },
    { url: 'https://images.unsplash.com/photo-1513407030348-c983a97b98d8?w=800', city: 'Tokyo', country: 'Japan' },
    { url: 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=800', city: 'Rome', country: 'Italy' },
    { url: 'https://images.unsplash.com/photo-1596422846543-75c6fc197f07?w=800', city: 'Bali', country: 'Indonesia' },
    { url: 'https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?w=800', city: 'Sydney', country: 'Australia' },
    { url: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=800', city: 'Dubai', country: 'United Arab Emirates' },
    { url: 'https://images.unsplash.com/photo-1533929736458-ca588d08c8be?w=800', city: 'London', country: 'United Kingdom' },
    { url: 'https://images.unsplash.com/photo-1518548419970-58e3b4079ab2?w=800', city: 'Barcelona', country: 'Spain' },
    { url: 'https://images.unsplash.com/photo-1729476266005-b14af8894f5e?w=800', city: 'Torres del Paine', country: 'Chile' },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <Header transparent />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center">
        <div className="absolute inset-0 z-0">
          <img src={heroImage} alt="Travel landscape" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-teal-900/70 via-teal-900/40 to-teal-900/20" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-32">
          <div className="max-w-2xl">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="flex items-center gap-3 mb-6">
              <img src="/logo.jpg" alt="Triple A" className="w-14 h-14 rounded-xl object-contain bg-white/90 p-1" />
              <span className="font-serif text-base tracking-wider uppercase font-bold"><span className="text-white">Triple A</span> <span className="text-amber-300">Travel Solutions</span></span>
            </motion.div>

            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
              className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mb-6">
              Your One-Stop
              <br />
              <span className="text-amber-300">Travel Portal</span>
            </motion.h1>

            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
              className="text-white/80 text-base md:text-lg mb-8 max-w-lg">
              Plan multi-country trips, track budgets, get visa info, generate packing lists, and book flights & hotels -- all powered by AI.
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }} className="flex flex-col sm:flex-row gap-4">
              <Button className="bg-amber-400 hover:bg-amber-500 text-teal-900 text-lg px-8 py-6 rounded-full font-bold flex items-center gap-2 shadow-xl hover:shadow-2xl transition-all"
                onClick={handleGetStarted} data-testid="hero-cta-btn">
                Start Planning <ArrowRight className="w-5 h-5" />
              </Button>
              <Button variant="outline" className="border-white/50 text-white hover:bg-white/10 hover:text-white px-8 py-6 rounded-full"
                onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })} data-testid="learn-more-btn">
                Learn More
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6 md:px-12 lg:px-24 bg-gradient-to-b from-ocean/95 to-ocean-light/90">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">Everything You Need for the Perfect Trip</h2>
            <p className="text-white/70 max-w-2xl mx-auto">Your complete travel companion -- from planning to booking, all in one place</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div key={feature.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/15 hover:bg-white/15 hover:-translate-y-1 transition-all duration-300"
                data-testid={`feature-card-${index}`}>
                <div className="w-12 h-12 bg-gradient-to-br from-gold to-gold-light rounded-xl flex items-center justify-center text-ocean mb-4">
                  {feature.icon}
                </div>
                <h3 className="font-semibold text-white text-lg mb-2">{feature.title}</h3>
                <p className="text-white/70 text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Destinations Carousel */}
      <section className="py-20 px-6 md:px-12 lg:px-24 bg-gradient-to-b from-ocean-light/90 to-sky/70">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="flex items-center justify-between mb-8">
            <div>
              <h2 className="font-serif text-3xl md:text-4xl text-white drop-shadow-lg mb-2">Popular Destinations</h2>
              <p className="text-white/70">Click on a destination to start planning your trip</p>
            </div>
            <div className="hidden md:flex gap-2">
              <Button variant="outline" size="icon" onClick={() => scrollCarousel('left')} disabled={!canScrollLeft}
                className="rounded-full border-white/30 text-white hover:bg-white/10 disabled:opacity-30" data-testid="carousel-left-btn">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <Button variant="outline" size="icon" onClick={() => scrollCarousel('right')} disabled={!canScrollRight}
                className="rounded-full border-white/30 text-white hover:bg-white/10 disabled:opacity-30" data-testid="carousel-right-btn">
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </motion.div>

          <div className="relative">
            <div ref={carouselRef} onScroll={handleScroll} className="flex gap-6 overflow-x-auto scrollbar-hide pb-4 snap-x snap-mandatory"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {destinations.map((dest, index) => (
                <motion.div key={`${dest.city}-${dest.country}`} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ duration: 0.5, delay: index * 0.05 }}
                  onClick={() => handleDestinationClick(dest)}
                  className="group relative flex-shrink-0 w-72 aspect-[3/4] rounded-2xl overflow-hidden cursor-pointer snap-start shadow-md hover:shadow-2xl transition-all duration-300"
                  data-testid={`destination-card-${index}`}>
                  <img src={dest.url} alt={`${dest.city}, ${dest.country}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="font-serif text-xl text-white mb-1">{dest.city}</h3>
                    <p className="text-white/70 text-sm flex items-center gap-1"><MapPin className="w-3 h-3" />{dest.country}</p>
                  </div>
                  <div className="absolute inset-0 bg-teal-600/0 group-hover:bg-teal-600/20 transition-colors duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <span className="bg-white text-teal-700 px-4 py-2 rounded-full font-medium text-sm transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                      Plan Trip
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6 md:px-12 lg:px-24 bg-gradient-to-br from-teal-800 to-teal-900 text-white">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-serif text-3xl md:text-4xl mb-12">How It Works</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { step: '1', title: 'Tell Us Your Dream', desc: 'Select destinations, dates, budget, and travel style. Plan multi-country trips with ease.' },
                { step: '2', title: 'AI Crafts Your Trip', desc: 'Our AI generates a day-by-day itinerary with visa info, packing lists, and budget estimates.' },
                { step: '3', title: 'Book & Go', desc: 'Book flights and hotels directly, download your itinerary, and share with travel companions.' },
              ].map((item) => (
                <motion.div key={item.step} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: Number(item.step) * 0.15 - 0.15 }}>
                  <div className="w-14 h-14 bg-amber-400 text-teal-900 rounded-full flex items-center justify-center font-bold text-xl mx-auto mb-4">{item.step}</div>
                  <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                  <p className="text-teal-100/80 text-sm">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 md:px-12 lg:px-24 bg-gradient-to-b from-ocean/90 to-ocean-light/80">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-serif text-3xl md:text-4xl text-white drop-shadow-lg mb-4">Ready to Start Your Adventure?</h2>
            <p className="text-white/70 mb-8 max-w-xl mx-auto">
              Join thousands of travelers who plan their perfect trips with Triple A Travel Solutions.
            </p>
            <Button className="bg-gold hover:bg-gold-light text-ocean text-lg px-10 py-6 rounded-full font-bold shadow-xl hover:shadow-2xl transition-all"
              onClick={handleGetStarted} data-testid="cta-btn">
              Create Your Itinerary
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Traveller Reviews */}
      <LandingReviews />
    </div>
  );
};

export default LandingPage;
